import { Divider } from '@mui/material';
import { ProductAccordion } from '@tix-ticketing/ui-components';
import { FormattedMessage } from 'react-intl';
import type { OrderResponse } from 'api/generated/types.gen';
import { CartProductLine } from 'components/cart/cart-product-line';
import { useEventProductsAdd } from 'components/cart/use-event-products-add';
import { useChangeProductQuantity } from 'components/products/use-change-product-quantity';
import { getEventProductLines, getProductLineMaximum, withQuantity } from 'lib/helpers/product-helpers';
import { useProductTexts } from 'lib/hooks/use-product-texts';

/**
 * The products the order holds for an event, under the event's tickets, with
 * steppers changing their quantities and a link to add more while the event
 * offers any; while changes are not allowed the lines are read-only. A change
 * shows before the backend confirms it. Renders nothing
 * while the order holds none: adding the first goes through the event card's
 * actions menu.
 */
export function CartEventProducts({
  order,
  eventId,
  inAside,
  allowChange
}: {
  order: OrderResponse | undefined;
  eventId: number;
  inAside: boolean;
  /** Whether products can be added or changed, which leaving the cart or a
   * changed order would get in the way of. */
  allowChange: boolean;
}) {
  const {
    addText,
    cartTitle
  } = useProductTexts(eventId);
  const {
    offered,
    addProducts
  } = useEventProductsAdd(eventId, allowChange);
  const {
    setQuantity,
    removeLine,
    pending
  } = useChangeProductQuantity(eventId);
  const lines = getEventProductLines(order, eventId).filter(line => pending[line.productVariantId] !== 0);
  if (lines.length === 0) return null;
  return <>
      {inAside && <Divider />}
      <ProductAccordion defaultExpanded={!inAside} inAside={inAside} title={cartTitle} addLabel={addText} addLabelShort={<FormattedMessage id="common.add" defaultMessage={[{
      "type": 0,
      "value": "Add"
    }]} />} onAdd={addProducts}>
        {lines.map(line => <CartProductLine key={line.productVariantId} line={withQuantity(line, pending[line.productVariantId] ?? line.quantity)} inAside={inAside} maximum={getProductLineMaximum(line, offered)} onChangeQuantity={allowChange ? quantity => setQuantity(line, quantity) : undefined} onRemove={allowChange ? () => removeLine(line) : undefined} />)}
      </ProductAccordion>
    </>;
}